import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { reminderApi } from '../../services/api';

const TemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    is_default: false
  });
  
  // Additional state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch template data if in edit mode
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!isEditMode) return;
      
      setLoading(true);
      try {
        const response = await reminderApi.getTemplateById(id);
        setFormData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching template:', err);
        setError('Failed to load template. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplate();
  }, [id, isEditMode]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode) {
        await reminderApi.updateTemplate(id, formData);
      } else {
        await reminderApi.createTemplate(formData);
      }
      navigate('/reminder-templates');
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please check your inputs and try again.');
      setSubmitting(false);
    }
  };
  
  // Template variables help
  const templateVariables = [
    { name: 'member.name', description: 'Member\'s name' },
    { name: 'member.username', description: 'Member\'s username' },
    { name: 'member.email', description: 'Member\'s email address' },
    { name: 'payment_status.current_fee', description: 'Current monthly fee' },
    { name: 'payment_status.paid_until', description: 'Date until which the member has paid' },
    { name: 'payment_status.months_paid_in_advance', description: 'Number of months paid in advance' },
    { name: 'payment_status.missing_months', description: 'List of months with missing payments' },
    { name: 'current_date', description: 'Current date (DD.MM.YYYY)' },
    { name: 'app_name', description: 'Application name' }
  ];
  
  // Template example - using a string to avoid JavaScript template literal interpretation
  const templateExample = '<html>\n' +
'<body>\n' +
'  <h2>Payment Reminder</h2>\n' +
'  <p>Hello {{ member.name if member.name else member.username }},</p>\n' +
'  \n' +
'  <p>This is a friendly reminder about your subscription to {{ app_name }}.</p>\n' +
'  \n' +
'  {% if payment_status.paid_until %}\n' +
'  <p>Your current subscription is paid until: <strong>{{ payment_status.paid_until }}</strong></p>\n' +
'  {% else %}\n' +
'  <p>We don\'t have any record of your payment.</p>\n' +
'  {% endif %}\n' +
'  \n' +
'  {% if payment_status.missing_months %}\n' +
'  <p>You have missing payments for the following months:</p>\n' +
'  <ul>\n' +
'  {% for month in payment_status.missing_months %}\n' +
'    <li>{{ month }}</li>\n' +
'  {% endfor %}\n' +
'  </ul>\n' +
'  {% endif %}\n' +
'  \n' +
'  <p>Your current monthly fee is: <strong>{{ payment_status.current_fee }}</strong></p>\n' +
'  \n' +
'  <p>Please make your payment at your earliest convenience.</p>\n' +
'  \n' +
'  <p>Thank you for your continued support!</p>\n' +
'  \n' +
'  <p>Best regards,<br>\n' +
'  {{ app_name }} Team</p>\n' +
'</body>\n' +
'</html>';
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="template-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isEditMode ? 'Edit Template' : 'New Template'}</h1>
        <Link to="/reminder-templates" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-1"></i> Back to Templates
        </Link>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Template Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="subject" className="form-label">Email Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                  <div className="form-text">
                    You can use template variables like {'{{ app_name }}'} in the subject.
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="body" className="form-label">Template Body (HTML)</label>
                  <textarea
                    className="form-control font-monospace"
                    id="body"
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    rows="15"
                    required
                  ></textarea>
                  <div className="form-text">
                    HTML is supported. Use template variables and Jinja2 syntax for dynamic content.
                  </div>
                </div>
                
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_default"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="is_default">
                    Set as default template
                  </label>
                  <div className="form-text">
                    The default template will be used when creating new reminders.
                  </div>
                </div>
                
                <div className="d-flex justify-content-between mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-1"></i> {isEditMode ? 'Update' : 'Create'} Template
                      </>
                    )}
                  </button>
                  
                  <Link to="/reminder-templates" className="btn btn-outline-secondary">
                    Cancel
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <div className="col-lg-4">
          <div className="card shadow-sm mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Template Variables</h5>
            </div>
            <div className="card-body">
              <p className="card-text">
                You can use these variables in your template:
              </p>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {templateVariables.map((variable, index) => (
                    <tr key={index}>
                      <td><code>{`{{ ${variable.name} }}`}</code></td>
                      <td>{variable.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="card shadow-sm">
            <div className="card-header">
              <h5 className="card-title mb-0">Template Example</h5>
            </div>
            <div className="card-body">
              <p className="card-text">
                Here's an example template you can use as a starting point:
              </p>
              <div className="bg-light p-2 rounded">
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                  {templateExample}
                </pre>
              </div>
              <button
                className="btn btn-sm btn-outline-secondary mt-2"
                onClick={() => setFormData(prev => ({ ...prev, body: templateExample }))}
              >
                <i className="fas fa-copy me-1"></i> Use This Example
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateForm;
